import type { ReactElement } from 'react';
import { SwitchButton, SwitchField, Text, type SwitchFieldProps } from 'react-aria-components';
import s from './fields.module.css';

export interface SwitchProps extends Omit<SwitchFieldProps, 'children' | 'className'> {
  /** Visible label; the switch is named by it. */
  label: string;
  /** Help text under the switch. */
  description?: string;
  /** Show the state in words next to the switch ("On" / "Off"). Status never by colour alone. */
  showState?: boolean;
  /**
   * Keep the label for screen readers only, where a column heading already says what it is (a
   * table's "On" column). The state word stays visible.
   */
  labelHidden?: boolean;
}

/** An on/off switch (`role="switch"`, `aria-checked`), built on React Aria SwitchField. */
export function Switch({
  label,
  description,
  showState = true,
  labelHidden = false,
  ...rest
}: SwitchProps): ReactElement {
  return (
    <SwitchField {...rest} className={s.switchField}>
      <SwitchButton className={s.switch}>
        {({ isSelected }) => (
          <>
            <span className={s.track} aria-hidden="true">
              <span className={s.thumb} />
            </span>
            {labelHidden ? (
              <span>
                <span className="visually-hidden">{label}</span>
                {showState && <span className={s.stateWord}>{isSelected ? 'On' : 'Off'}</span>}
              </span>
            ) : (
              <span>
                {label}
                {showState && <span className={s.stateWord}> · {isSelected ? 'On' : 'Off'}</span>}
              </span>
            )}
          </>
        )}
      </SwitchButton>
      {description !== undefined && (
        <Text slot="description" className={s.description}>
          {description}
        </Text>
      )}
    </SwitchField>
  );
}
